/**
 * The base-view stylesheet, assembled.
 *
 * It used to be ONE 1160-line template literal in `styles.ts`, which every UI
 * feature appended to — so two people working on different panels edited the
 * same file, and a backtick typed into a comment anywhere in it broke the
 * whole build. It is now one file per area, concatenated here.
 *
 * Still exactly one injected style element: the split is about who owns which
 * rules, not about how many sheets the document carries.
 * `styles.tokens.test.ts` audits the assembled string, so a token typo in any
 * slice fails exactly the way it did before.
 *
 * ORDER MATTERS where it matters: `capacity` declares the .bv-cap-ok /
 * -warn / -critical fills that hud, paperdoll and item all key off, and CSS
 * resolves equal-specificity ties by source order — so a slice that has to
 * win keeps its place in this list rather than relying on specificity.
 */
import root from './root.css';
import window_chrome from './window.css';
import close_button from './close.css';
import hud from './hud.css';
import action_bar from './action-bar.css';
import capacity from './capacity.css';
import minimap from './minimap.css';
import paperdoll from './paperdoll.css';
import item from './item.css';
import location from './location.css';
import arena from './arena.css';
import chat from './chat.css';
import commerce from './commerce.css';
import vendor from './vendor.css';
import atelier from './atelier.css';
import black_market from './black-market.css';
import world_map from './world-map.css';
import raid_panel from './raid-panel.css';
import home from './home.css';
import tutorial from './tutorial.css';
import duel from './duel.css';
import hotbar from './hotbar.css';
import errors from './errors.css';
import dialog from './dialog.css';
import tutorial_map from './tutorial-map.css';
import coin from './coin.css';
import quest from './quest.css';
import quest_tracker from './quest-tracker.css';

const STYLE_ID = 'bv-styles';

const CSS = [
  root,
  // After root (which no longer carries the conversation rules) and before the
  // panel slices: it is a self-contained pair of plates and overrides nothing.
  dialog,
  // Early: every panel slice below may override the shared frame for its own
  // window, and CSS resolves equal-specificity ties by source order.
  window_chrome,
  // Right after the window frame: the one close cross every window wears. Slices
  // below only PLACE it; none may restyle it.
  close_button,
  hud,
  action_bar,
  // After the action bar: it adds the count badge to a button that slice draws.
  quest_tracker,
  tutorial_map,
  coin,
  quest,
  capacity,
  minimap,
  paperdoll,
  item,
  location,
  arena,
  chat,
  commerce,
  // After commerce and item: restyles the vendor restock plate and trade mark.
  vendor,
  // After commerce: the Atelier restyles parts of the shop window it lives in.
  atelier,
  // After commerce, beside the Atelier: the shop window's third face.
  black_market,
  world_map,
  raid_panel,
  home,
  // The duel HUD belongs to `arena-view.ts`, which mounts on its own and has no
  // panels to fight with — its layers are local and start at 30.
  duel,
  // After `duel`: the hotbar sits at 32, over the HUD's own furniture, and
  // both are mounted by the same view.
  hotbar,
  // After every slice that styles its own error plate (window, paperdoll, home,
  // duel), so one recipe wins for all of them at equal specificity.
  errors,
  // Last: the walkthrough paints over every panel above (z-index 1150) and
  // its rules should not be fought by a later slice's source order.
  tutorial,
].join('\n');

export function injectStyles(): () => void {
  if (document.getElementById(STYLE_ID)) return () => {};
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
  return () => style.remove();
}

/** The raw CSS, for `styles.tokens.test.ts`'s token audit. */
export const BASE_VIEW_CSS = CSS;
